/** Where a team member works, as stored in organization_members.work_location. */
export type WorkLocation = {
  city: string;
  country: string;
  lat: number;
  lng: number;
  /** IANA time zone, for the member's local time. */
  tz: string;
};

const city = (
  name: string,
  country: string,
  lat: number,
  lng: number,
  tz: string,
): WorkLocation => ({ city: name, country, lat, lng, tz });

/** Cities members can pick for the Team Map (Latin America and the US). */
export const TEAM_MAP_CITIES: WorkLocation[] = [
  city("Mexico City", "Mexico", 19.43, -99.13, "America/Mexico_City"),
  city("Guadalajara", "Mexico", 20.67, -103.35, "America/Mexico_City"),
  city("Monterrey", "Mexico", 25.69, -100.32, "America/Monterrey"),
  city("Tijuana", "Mexico", 32.51, -117.04, "America/Tijuana"),
  city("Cancún", "Mexico", 21.16, -86.85, "America/Cancun"),
  city("Guatemala City", "Guatemala", 14.63, -90.51, "America/Guatemala"),
  city("San Salvador", "El Salvador", 13.69, -89.22, "America/El_Salvador"),
  city("Tegucigalpa", "Honduras", 14.07, -87.19, "America/Tegucigalpa"),
  city("San Pedro Sula", "Honduras", 15.5, -88.03, "America/Tegucigalpa"),
  city("Managua", "Nicaragua", 12.11, -86.24, "America/Managua"),
  city("San José", "Costa Rica", 9.93, -84.08, "America/Costa_Rica"),
  city("Panama City", "Panama", 8.98, -79.52, "America/Panama"),
  city("Havana", "Cuba", 23.11, -82.37, "America/Havana"),
  city(
    "Santo Domingo",
    "Dominican Republic",
    18.49,
    -69.93,
    "America/Santo_Domingo",
  ),
  city(
    "Santiago de los Caballeros",
    "Dominican Republic",
    19.45,
    -70.7,
    "America/Santo_Domingo",
  ),
  city("San Juan", "Puerto Rico", 18.47, -66.11, "America/Puerto_Rico"),
  city("Bogotá", "Colombia", 4.71, -74.07, "America/Bogota"),
  city("Medellín", "Colombia", 6.24, -75.58, "America/Bogota"),
  city("Cali", "Colombia", 3.45, -76.53, "America/Bogota"),
  city("Barranquilla", "Colombia", 10.96, -74.8, "America/Bogota"),
  city("Caracas", "Venezuela", 10.49, -66.88, "America/Caracas"),
  city("Maracaibo", "Venezuela", 10.64, -71.63, "America/Caracas"),
  city("Quito", "Ecuador", -0.18, -78.47, "America/Guayaquil"),
  city("Guayaquil", "Ecuador", -2.17, -79.92, "America/Guayaquil"),
  city("Cuenca", "Ecuador", -2.9, -79.0, "America/Guayaquil"),
  city("Macas", "Ecuador", -2.31, -78.12, "America/Guayaquil"),
  city("Lima", "Peru", -12.05, -77.04, "America/Lima"),
  city("Arequipa", "Peru", -16.41, -71.54, "America/Lima"),
  city("La Paz", "Bolivia", -16.5, -68.15, "America/La_Paz"),
  city("Santa Cruz", "Bolivia", -17.78, -63.18, "America/La_Paz"),
  city("Santiago", "Chile", -33.45, -70.67, "America/Santiago"),
  city(
    "Buenos Aires",
    "Argentina",
    -34.6,
    -58.38,
    "America/Argentina/Buenos_Aires",
  ),
  city("Córdoba", "Argentina", -31.42, -64.18, "America/Argentina/Cordoba"),
  city("Montevideo", "Uruguay", -34.9, -56.16, "America/Montevideo"),
  city("Asunción", "Paraguay", -25.26, -57.58, "America/Asuncion"),
  city("São Paulo", "Brazil", -23.55, -46.63, "America/Sao_Paulo"),
  city("Rio de Janeiro", "Brazil", -22.91, -43.17, "America/Sao_Paulo"),
  city("Brasília", "Brazil", -15.79, -47.88, "America/Sao_Paulo"),
  city("Fortaleza", "Brazil", -3.73, -38.52, "America/Fortaleza"),
  city("Manaus", "Brazil", -3.12, -60.02, "America/Manaus"),
  // LBS office building, 1200 Summer St.
  city("Stamford, CT", "United States", 41.063, -73.539, "America/New_York"),
  city("New York, NY", "United States", 40.71, -74.01, "America/New_York"),
  city("Newark, NJ", "United States", 40.74, -74.17, "America/New_York"),
  city("Boston, MA", "United States", 42.36, -71.06, "America/New_York"),
  city("Miami, FL", "United States", 25.76, -80.19, "America/New_York"),
  city("Orlando, FL", "United States", 28.54, -81.38, "America/New_York"),
  city("Atlanta, GA", "United States", 33.75, -84.39, "America/New_York"),
  city("Chicago, IL", "United States", 41.88, -87.63, "America/Chicago"),
  city("Houston, TX", "United States", 29.76, -95.37, "America/Chicago"),
  city("Dallas, TX", "United States", 32.78, -96.8, "America/Chicago"),
  city("Denver, CO", "United States", 39.74, -104.99, "America/Denver"),
  city("Phoenix, AZ", "United States", 33.45, -112.07, "America/Phoenix"),
  city(
    "Los Angeles, CA",
    "United States",
    34.05,
    -118.24,
    "America/Los_Angeles",
  ),
];

export const workLocationLabel = (location: WorkLocation) =>
  `${location.city}, ${location.country}`;

/** A stored location, checked: null when missing or malformed. */
export const parseWorkLocation = (value: unknown): WorkLocation | null => {
  if (!value || typeof value !== "object") return null;
  const raw = value as Partial<WorkLocation>;
  if (
    typeof raw.city !== "string" ||
    typeof raw.lat !== "number" ||
    typeof raw.lng !== "number"
  ) {
    return null;
  }
  return {
    city: raw.city,
    country: typeof raw.country === "string" ? raw.country : "",
    lat: raw.lat,
    lng: raw.lng,
    tz: typeof raw.tz === "string" ? raw.tz : "UTC",
  };
};

/** "3:42 PM" in the member's time zone. */
export const localTimeIn = (tz: string, now = new Date()) => {
  try {
    return now.toLocaleTimeString("en-US", {
      timeZone: tz,
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return null;
  }
};
